import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { productApi } from '@/api/commercial/products';
import { deviceApi } from '@/api/production/devices';
import { useFormError } from '@/hooks/use-form-error';
import { toast } from '@/hooks/use-toast';
import { type UpdateDeviceFormData, updateDeviceSchema } from '@/schemas/production/device.schema';
import type { IDevice, UpdateDeviceDto } from '@/types/production/device';
import { mapProductNameToDeviceModel } from '@/utils/device-model-mapper';

interface UseDeviceUpdateFormProps {
  device: IDevice;
}

export function useDeviceUpdateForm({ device }: UseDeviceUpdateFormProps) {
  const { formError, showFormError, clearFormError } = useFormError();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: productsData } = useQuery({
    queryKey: ['products'],
    queryFn: () => productApi.findMany({ filter: {}, limit: 100 }),
  });

  const products = productsData?.docs || [];

  // Find the product that matches the current device model
  const currentProduct = products.find((p) => mapProductNameToDeviceModel(p.name) === device.model);

  const form = useForm<UpdateDeviceFormData>({
    resolver: zodResolver(updateDeviceSchema),
    defaultValues: {
      serial: device.equipment.serial,
      product_id: currentProduct?.id || '',
    },
  });

  const mutation = useMutation({
    mutationFn: (data: UpdateDeviceDto) => deviceApi.update(device.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['device', device.id] });
      toast({
        variant: 'success',
        title: 'Sucesso!',
        description: 'Equipamento atualizado com sucesso!',
      });
      navigate('/production/devices');
    },
    onError: () => {
      showFormError('Falha ao atualizar o equipamento!');
    },
  });

  const handleSubmit = form.handleSubmit((data) => {
    const product = products.find((p) => p.id === data.product_id);

    if (!product) {
      form.setError('product_id', { message: 'Produto não encontrado!' });
      return;
    }

    const deviceModel = mapProductNameToDeviceModel(product.name);

    if (!deviceModel) {
      form.setError('product_id', {
        message: `Produto "${product.name}" não possui mapeamento para modelo de dispositivo!`,
      });
      return;
    }

    const deviceData: UpdateDeviceDto = {
      equipment: {
        serial: data.serial,
        firmware: device.equipment.firmware, // Keep existing firmware
      },
      model: deviceModel as any,
    };

    mutation.mutate(deviceData);
  });

  return {
    formError,
    clearFormError,
    form,
    handleSubmit,
    isSubmitting: mutation.isPending,
    products,
  };
}
